import { Stack, Group, Text, ActionIcon, Tooltip } from "@mantine/core";
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useCallback, useMemo } from "react";
import {
  formatDateOnlyForDisplay,
  parseDateOnly,
} from "../../../utils/dateOnly";

const VISIBLE_CASE_COUNT = 7;

function formatQueueDateOfBirth(value: unknown): string {
  const dateOfBirth = parseDateOnly(value);
  return dateOfBirth ? formatDateOnlyForDisplay(dateOfBirth) : "";
}

interface QueueCase {
  id: string;
  caseNumber: string;
  patient?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
  };
  isBlocked?: boolean;
}

interface CaseQueueWidgetProps {
  currentCaseId: string;
  queueCases: QueueCase[];
}

export function CaseQueueWidget({
  currentCaseId,
  queueCases,
}: CaseQueueWidgetProps) {
  const navigate = useNavigate();
  const available = useMemo(
    () => queueCases.filter((caseItem) => !caseItem.isBlocked),
    [queueCases]
  );
  const currentIdx = useMemo(
    () => available.findIndex((caseItem) => caseItem.id === currentCaseId),
    [available, currentCaseId]
  );
  const visible = useMemo(() => {
    const halfWindow = Math.floor(VISIBLE_CASE_COUNT / 2);
    let start = Math.max(0, currentIdx - halfWindow);
    const end = Math.min(available.length, start + VISIBLE_CASE_COUNT);
    start = Math.max(0, end - VISIBLE_CASE_COUNT);
    return available.slice(start, end);
  }, [available, currentIdx]);
  const previousCase = useMemo(
    () => (currentIdx > 0 ? available[currentIdx - 1] : undefined),
    [available, currentIdx]
  );
  const nextCase = useMemo(
    () =>
      currentIdx < available.length - 1 ? available[currentIdx + 1] : undefined,
    [available, currentIdx]
  );

  const handleCaseClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const caseId = event.currentTarget.dataset.caseId;
      if (caseId && caseId !== currentCaseId) {
        navigate(`/pathologist-review/${caseId}`);
      }
    },
    [currentCaseId, navigate]
  );

  const handlePreviousCase = useCallback(() => {
    if (previousCase) navigate(`/pathologist-review/${previousCase.id}`);
  }, [navigate, previousCase]);

  const handleNextCase = useCallback(() => {
    if (nextCase) navigate(`/pathologist-review/${nextCase.id}`);
  }, [navigate, nextCase]);

  if (available.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Queue is empty
      </Text>
    );
  }

  return (
    <Stack gap={2}>
      {visible.map((c) => {
        const isCurrent = c.id === currentCaseId;
        const firstName = c.patient?.firstName || "";
        const dob = formatQueueDateOfBirth(c.patient?.dateOfBirth);
        return (
          <Group
            key={c.id}
            data-case-id={c.id}
            gap="xs"
            wrap="nowrap"
            py={4}
            px={4}
            style={{
              borderRadius: 4,
              background: isCurrent ? "var(--mantine-color-blue-0)" : undefined,
              cursor: isCurrent ? "default" : "pointer",
            }}
            onClick={handleCaseClick}
          >
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Text
                size="xs"
                fw={isCurrent ? 700 : 500}
                c={isCurrent ? "blue" : undefined}
                truncate
              >
                {c.caseNumber}
              </Text>
              {firstName && (
                <Text size="xs" truncate>
                  {firstName}
                </Text>
              )}
              {dob && (
                <Text size="xs" c="dimmed">
                  {dob}
                </Text>
              )}
            </Stack>
          </Group>
        );
      })}

      <Group justify="space-between" mt={4}>
        <Text size="xs" c="dimmed">
          {currentIdx + 1} of {available.length}
        </Text>
        <Group gap={4}>
          <Tooltip label="Previous case" withArrow>
            <ActionIcon
              size="xs"
              variant="light"
              disabled={!previousCase}
              onClick={handlePreviousCase}
            >
              <IconArrowLeft size={12} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Next case" withArrow>
            <ActionIcon
              size="xs"
              variant="light"
              disabled={!nextCase}
              onClick={handleNextCase}
            >
              <IconArrowRight size={12} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Stack>
  );
}
